import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { HomeContent, HomeContentCard, HomeContentLink, HomeContentSection, DEFAULT_HOME_CONTENT } from '../types';

// Deep-clones the defaults so "Reset to default" and the initial state
// can't hand out a reference that later edits mutate in place.
function cloneDefaults(): HomeContent {
  return JSON.parse(JSON.stringify(DEFAULT_HOME_CONTENT)) as HomeContent;
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  hint?: string;
}

const Field = ({ label, value, onChange, multiline, hint }: FieldProps) => (
  <div className="form-group">
    <label>{label}</label>
    {multiline ? (
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} />
    )}
    {hint && <small style={{ color: 'var(--text-secondary)' }}>{hint}</small>}
  </div>
);

/**
 * Add/remove/reorder controls shared by every repeating list on the page
 * (hero steps, section cards, contact rows). `renderItem` draws one
 * entry's fields; everything else — the row chrome and the list
 * operations — is the same for all of them.
 */
function ListEditor<T>({
  items,
  onChange,
  blank,
  label,
  renderItem
}: {
  items: T[];
  onChange: (next: T[]) => void;
  blank: () => T;
  label: string;
  renderItem: (item: T, update: (next: T) => void) => React.ReactNode;
}) {
  const replaceAt = (index: number, next: T) => onChange(items.map((item, i) => (i === index ? next : item)));
  const removeAt = (index: number) => onChange(items.filter((_, i) => i !== index));
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <>
      {items.map((item, index) => (
        <div
          key={index}
          style={{
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            padding: '1rem',
            marginBottom: '0.75rem'
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}
          >
            <strong style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {label} {index + 1}
            </strong>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              <button
                className="btn btn-outline btn-sm"
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label={`Move ${label} ${index + 1} up`}
              >
                ↑
              </button>
              <button
                className="btn btn-outline btn-sm"
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === items.length - 1}
                aria-label={`Move ${label} ${index + 1} down`}
              >
                ↓
              </button>
              <button
                className="btn btn-outline btn-sm"
                type="button"
                onClick={() => removeAt(index)}
                aria-label={`Remove ${label} ${index + 1}`}
              >
                Remove
              </button>
            </div>
          </div>
          {renderItem(item, (next) => replaceAt(index, next))}
        </div>
      ))}
      <button className="btn btn-outline btn-sm" type="button" onClick={() => onChange([...items, blank()])}>
        Add {label.toLowerCase()}
      </button>
    </>
  );
}

const HomeContentPage = () => {
  const { user } = useAuth();
  const [content, setContent] = useState<HomeContent>(cloneDefaults());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Confirmed in a modal rather than an inline banner: Save sits at the
  // bottom of a long form, so a banner at the top of the page lands
  // off-screen and the Super Admin can't tell whether it saved. Same
  // pattern as the profile page's "Profile is Saved".
  const [showSavedModal, setShowSavedModal] = useState(false);

  useEffect(() => {
    apiService
      .getHomeContent()
      .then(setContent)
      .catch((err: any) => setError(err.message || 'Failed to load home page content'))
      .finally(() => setLoading(false));
  }, []);

  // Patches one top-level section, leaving the rest of the page alone.
  // The cast is for the computed key: with K generic, TypeScript widens
  // the literal to an index signature and stops seeing it as HomeContent,
  // even though every field is present via the spread of `prev`.
  const patch = <K extends keyof HomeContent>(key: K, value: Partial<HomeContent[K]>) => {
    setContent((prev) => ({ ...prev, [key]: { ...prev[key], ...value } }) as HomeContent);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      // Re-render from what the server stored, not the local copy — it
      // strips anything outside HomeContent's shape, so this is what the
      // public page will actually show.
      setContent(await apiService.updateHomeContent(content));
      setShowSavedModal(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save home page content');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setContent(cloneDefaults());
    setError('');
  };

  // Every section of the page below About shares the same
  // title/intro/cards shape, so one editor covers all three.
  const sectionEditor = (key: 'about' | 'programs' | 'guidelines', heading: string) => {
    const data: HomeContentSection = content[key];
    return (
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3>{heading}</h3>
        </div>
        <Field label="Heading" value={data.title} onChange={(title) => patch(key, { title })} />
        <Field label="Intro" value={data.intro} onChange={(intro) => patch(key, { intro })} multiline />
        <ListEditor<HomeContentCard>
          items={data.cards}
          label="Card"
          blank={() => ({ title: '', body: '' })}
          onChange={(cards) => patch(key, { cards })}
          renderItem={(card, update) => (
            <>
              <Field label="Card title" value={card.title} onChange={(title) => update({ ...card, title })} />
              <Field label="Card text" value={card.body} onChange={(body) => update({ ...card, body })} multiline />
            </>
          )}
        />
      </div>
    );
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">Home Page</div>
        <div className="navbar-actions">
          <div className="user-menu">
            <span>👤</span>
            <span>{user ? `${user.firstName} ${user.lastName}` : ''}</span>
          </div>
        </div>
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Home Page Content</h1>
          <p>
            Every text on the public landing page. Changes go live as soon as you save —{' '}
            <Link to="/" style={{ textDecoration: 'underline' }}>
              view the page
            </Link>
            .
          </p>
        </div>

        {error && (
          <div className="alert-error" style={{ padding: '1rem', marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <form onSubmit={handleSave}>
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3>Branding</h3>
              </div>
              <Field
                label="Acronym"
                value={content.brand.acronym}
                onChange={(acronym) => patch('brand', { acronym })}
                hint="Shown next to the municipal seal in the header."
              />
              <Field
                label="Full program name"
                value={content.brand.fullName}
                onChange={(fullName) => patch('brand', { fullName })}
              />
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3>Navigation</h3>
              </div>
              <Field label="Home link" value={content.nav.home} onChange={(home) => patch('nav', { home })} />
              <Field label="About link" value={content.nav.about} onChange={(about) => patch('nav', { about })} />
              <Field
                label="Programs link"
                value={content.nav.programs}
                onChange={(programs) => patch('nav', { programs })}
              />
              <Field
                label="Guidelines link"
                value={content.nav.guidelines}
                onChange={(guidelines) => patch('nav', { guidelines })}
              />
              <Field
                label="Contact link"
                value={content.nav.contact}
                onChange={(contact) => patch('nav', { contact })}
              />
              <Field label="Sign In button" value={content.nav.signIn} onChange={(signIn) => patch('nav', { signIn })} />
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3>Hero</h3>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
                The headline is three pieces so the middle one can be shown in green: "{content.hero.titleLead}{' '}
                <span style={{ color: '#34D399' }}>{content.hero.titleAccent}</span> {content.hero.titleRest}"
              </p>
              <Field
                label="Headline start"
                value={content.hero.titleLead}
                onChange={(titleLead) => patch('hero', { titleLead })}
              />
              <Field
                label="Headline highlighted word"
                value={content.hero.titleAccent}
                onChange={(titleAccent) => patch('hero', { titleAccent })}
              />
              <Field
                label="Headline end"
                value={content.hero.titleRest}
                onChange={(titleRest) => patch('hero', { titleRest })}
              />
              <Field
                label="Description"
                value={content.hero.description}
                onChange={(description) => patch('hero', { description })}
                multiline
              />
              <Field
                label="Primary button"
                value={content.hero.primaryCta}
                onChange={(primaryCta) => patch('hero', { primaryCta })}
              />
              <Field
                label="Secondary button"
                value={content.hero.secondaryCta}
                onChange={(secondaryCta) => patch('hero', { secondaryCta })}
              />

              <div className="form-group">
                <label>Steps</label>
                <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                  The short words beside the headline. Each gets an icon automatically, in order.
                </small>
                <ListEditor<string>
                  items={content.hero.steps}
                  label="Step"
                  blank={() => ''}
                  onChange={(steps) => patch('hero', { steps })}
                  renderItem={(step, update) => <Field label="Text" value={step} onChange={update} />}
                />
              </div>

              <div className="form-group">
                <label>Reassurance panel</label>
                <ListEditor<HomeContentCard>
                  items={content.hero.highlights}
                  label="Item"
                  blank={() => ({ title: '', body: '' })}
                  onChange={(highlights) => patch('hero', { highlights })}
                  renderItem={(item, update) => (
                    <>
                      <Field label="Title" value={item.title} onChange={(title) => update({ ...item, title })} />
                      <Field label="Text" value={item.body} onChange={(body) => update({ ...item, body })} />
                    </>
                  )}
                />
              </div>
            </div>

            {sectionEditor('about', 'About')}
            {sectionEditor('programs', 'Programs')}
            {sectionEditor('guidelines', 'Guidelines')}

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3>Contact</h3>
              </div>
              <Field
                label="Heading"
                value={content.contact.title}
                onChange={(title) => patch('contact', { title })}
              />
              <Field
                label="Intro"
                value={content.contact.intro}
                onChange={(intro) => patch('contact', { intro })}
                multiline
              />
              <ListEditor<HomeContentLink>
                items={content.contact.details}
                label="Detail"
                blank={() => ({ label: '', value: '' })}
                onChange={(details) => patch('contact', { details })}
                renderItem={(detail, update) => (
                  <>
                    <Field
                      label="Label"
                      value={detail.label}
                      onChange={(label) => update({ ...detail, label })}
                      hint="e.g. Office, Address, Email"
                    />
                    <Field label="Value" value={detail.value} onChange={(value) => update({ ...detail, value })} />
                  </>
                )}
              />
            </div>

            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <div className="card-header">
                <h3>Footer</h3>
              </div>
              <Field label="Footer note" value={content.footer.note} onChange={(note) => patch('footer', { note })} />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Home Page'}
              </button>
              {/* Only fills the form — nothing is stored until Save, so a
                  misclick is undone by reloading the page. */}
              <button className="btn btn-outline" type="button" onClick={handleReset} disabled={saving}>
                Reset to default wording
              </button>
            </div>
          </form>
        )}
      </div>

      {showSavedModal && (
        <Modal title="Home Page is Saved" onClose={() => setShowSavedModal(false)}>
          <p>The home page has been updated. Visitors see the new wording immediately.</p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
            <button className="btn btn-primary" type="button" onClick={() => setShowSavedModal(false)}>
              OK
            </button>
            {/* Opens in a new tab so the editor and everything typed into
                it survives going off to look at the result. BASE_URL
                rather than a bare "/" so this still points at the app if
                it's ever served from a sub-path. */}
            <a className="btn btn-outline" href={import.meta.env.BASE_URL} target="_blank" rel="noopener noreferrer">
              View the page
            </a>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default HomeContentPage;
